import { Toaster } from "@/components/ui/toaster";
import { RequireRole } from "@/components/auth/RequireRole";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/lib/auth";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Directory from "./pages/Directory";
import Connections from "./pages/Connections";
import Admin from "./pages/Admin";
import AdminAssignments from "./pages/AdminAssignments";
import AdminImportHistory from "./pages/AdminImportHistory";
import AdminHelpArticlesList from "./pages/admin/help/ArticlesList";
import AdminHelpArticleEditor from "./pages/admin/help/ArticleEditor";
import AdminHelpCategoriesManagement from "./pages/admin/help/CategoriesManagement";
import AdminHelpArticleAnalytics from "./pages/admin/help/ArticleAnalytics";
import Schedule from "./pages/Schedule";
import ScheduleCall from "./pages/ScheduleCall";
import Announcements from "./pages/Announcements";
import Curricula from "./pages/Curricula";
import CurriculumDetail from "./pages/CurriculumDetail";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import Discussions from "./pages/Discussions";
import InstructorDashboard from "./pages/InstructorDashboard";
import CohortDetail from "./pages/CohortDetail";
import ReviewQueue from "./pages/ReviewQueue";
import SubmissionReview from "./pages/SubmissionReview";
import CourseEditor from "./pages/CourseEditor";
import Resources from "./pages/Resources";
import CertificateDemo from "./pages/CertificateDemo";
import NotFound from "./pages/NotFound";
import News from "./pages/News";
import Events from "./pages/Events";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import PitchDeck from "./pages/PitchDeck";
import PitchDeckForms from "./pages/PitchDeckForms";
import PitchDeckGenerate from "./pages/PitchDeckGenerate";
import Share from "./pages/Share";
import SurveyPage from "./pages/SurveyPage";
import DevSecurityTest from "./pages/DevSecurityTest";
import TopShopLanding from "./pages/topshop/TopShopLanding";
import TopShopHub from "./pages/topshop/TopShopHub";
import AdminTopShop from "./pages/admin/topshop/AdminTopShop";
import AdminAccess from "./pages/admin/access/AdminAccess";
import SponsorView from "./pages/sponsor/SponsorView";

const queryClient = new QueryClient();
const TOPSHOP_HOSTS = ["topshopchallenge.com", "www.topshopchallenge.com"];
const isTopShopHost = TOPSHOP_HOSTS.includes(window.location.hostname);

const GA4RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
      });
    }
  }, [location]);
  return null;
};

const TopShopHostRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    window.location.replace(`https://eabconnect.com${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        {/* <InstallPrompt /> is intentionally NOT mounted.
            index.html has the PWA manifest commented out and actively
            unregisters all service workers + deletes all caches, so there is no
            installable app and no offline capability. The prompt's iOS branch
            does not depend on beforeinstallprompt — it fired unconditionally 5
            seconds after load, advertising "offline functionality" that cannot
            exist; following it yields a plain bookmark, not a standalone app.
            Re-mount this only together with re-enabling the manifest and
            registering a service worker. public/manifest.json is already a
            complete, valid 28-icon file (consider start_url "/dashboard"). */}
        <BrowserRouter>
          <GA4RouteTracker />
          {isTopShopHost ? (
            <Routes>
              <Route path="/" element={<TopShopLanding />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="*" element={<TopShopHostRedirect />} />
            </Routes>
          ) : (
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/topshop" element={<TopShopLanding />} />
            <Route path="/topshop/hub" element={<RequireRole><TopShopHub /></RequireRole>} />
            <Route path="/topshop/application" element={<Navigate to="/topshop/hub?tab=application" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<RequireRole><Dashboard /></RequireRole>} />
            <Route path="/profile" element={<RequireRole><Profile /></RequireRole>} />
            <Route path="/directory" element={<RequireRole><Directory /></RequireRole>} />
            <Route path="/connections" element={<RequireRole><Connections /></RequireRole>} />
            <Route path="/admin" element={<RequireRole anyOf={["admin"]}><Admin /></RequireRole>} />
            <Route path="/admin/assignments" element={<RequireRole anyOf={["admin"]}><AdminAssignments /></RequireRole>} />
            <Route path="/admin/import-history" element={<RequireRole anyOf={["admin"]}><AdminImportHistory /></RequireRole>} />
            <Route path="/sponsor" element={<RequireRole anyOf={["sponsor", "admin"]} fallback="/dashboard"><SponsorView /></RequireRole>} />
            <Route path="/admin/top-shop" element={<RequireRole anyOf={["admin"]}><AdminTopShop /></RequireRole>} />
            <Route path="/admin/access" element={<RequireRole anyOf={["admin"]}><AdminAccess /></RequireRole>} />
            <Route path="/admin/help/articles" element={<RequireRole anyOf={["admin"]}><AdminHelpArticlesList /></RequireRole>} />
            <Route path="/admin/help/articles/new" element={<RequireRole anyOf={["admin"]}><AdminHelpArticleEditor /></RequireRole>} />
            <Route path="/admin/help/articles/:id/edit" element={<RequireRole anyOf={["admin"]}><AdminHelpArticleEditor /></RequireRole>} />
            <Route path="/admin/help/categories" element={<RequireRole anyOf={["admin"]}><AdminHelpCategoriesManagement /></RequireRole>} />
            <Route path="/admin/help/analytics" element={<RequireRole anyOf={["admin"]}><AdminHelpArticleAnalytics /></RequireRole>} />
            <Route path="/schedule" element={<RequireRole><Schedule /></RequireRole>} />
            <Route path="/schedule/:requestId" element={<RequireRole><ScheduleCall /></RequireRole>} />
            <Route path="/announcements" element={<RequireRole><Announcements /></RequireRole>} />
            <Route path="/curricula" element={<RequireRole><Curricula /></RequireRole>} />
            <Route path="/curricula/:id" element={<RequireRole><CurriculumDetail /></RequireRole>} />
            <Route path="/courses" element={<RequireRole><Courses /></RequireRole>} />
            <Route path="/courses/:id" element={<RequireRole><CourseDetail /></RequireRole>} />
            <Route path="/discussions" element={<RequireRole><Discussions /></RequireRole>} />
            <Route path="/instructor/courses" element={<RequireRole anyOf={["instructor", "admin"]}><InstructorDashboard /></RequireRole>} />
            <Route path="/instructor/cohorts/:id" element={<RequireRole anyOf={["instructor", "admin"]}><CohortDetail /></RequireRole>} />
            <Route path="/instructor/review-queue" element={<RequireRole anyOf={["instructor", "admin"]}><ReviewQueue /></RequireRole>} />
            <Route path="/instructor/submissions/:id" element={<RequireRole anyOf={["instructor", "admin"]}><SubmissionReview /></RequireRole>} />
            <Route path="/instructor/courses/:id/edit" element={<RequireRole anyOf={["instructor", "admin"]}><CourseEditor /></RequireRole>} />
            <Route path="/resources" element={<RequireRole><Resources /></RequireRole>} />
            
            <Route path="/certificate-demo" element={<RequireRole><CertificateDemo /></RequireRole>} />
            {/* Public: linked from the landing page header, so it must render
                for signed-out visitors. News.tsx has no auth dependency. */}
            <Route path="/news" element={<News />} />
            <Route path="/events" element={<RequireRole><Events /></RequireRole>} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/settings" element={<RequireRole><Settings /></RequireRole>} />
            <Route path="/help" element={<RequireRole><Help /></RequireRole>} />
            <Route path="/help/search" element={<RequireRole><Help /></RequireRole>} />
            <Route path="/help/category/:categoryId" element={<RequireRole><Help /></RequireRole>} />
            <Route path="/help/article/:articleId" element={<RequireRole><Help /></RequireRole>} />
            <Route path="/pitch-deck" element={<RequireRole><PitchDeck /></RequireRole>} />
            <Route path="/pitch-deck/forms" element={<RequireRole><PitchDeckForms /></RequireRole>} />
            <Route path="/pitch-deck/generate" element={<RequireRole><PitchDeckGenerate /></RequireRole>} />
            <Route path="/share/:shareToken" element={<Share />} />
            <Route path="/survey/:assignmentId" element={<RequireRole><SurveyPage /></RequireRole>} />
            <Route path="/dev/security-test" element={<DevSecurityTest />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          )}
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
